import useGetProjectByIdOverview from '@/api/queries/project/useGetProjectByIdOverview';
import { ServicePlanPeriodStatus } from '@/gql/graphql';
import usePreferredDateFormat from '@/hooks/usePreferredDateFormat';
import { Badge, ViewFieldset } from '@payaca/ambrosia';
import { DateFormats } from '@payaca/helpers/internationalHelper';
import { format } from 'date-fns';
import { FC } from 'react';
import { useParams } from 'react-router-dom';

const graphqlStatusToLocal = {
  ARRANGED: { label: 'Arranged', colour: 'yellow' },
  FULFILLED: { label: 'Complete', colour: 'green' },
  UNARRANGED: { label: 'Unarranged', colour: 'gray' },
} as const;

const getPeriodPhase = (startsAt: Date, endsAt: Date, now: Date) => {
  if (now < startsAt) return { label: 'Upcoming', colour: 'blue' } as const;
  if (now >= endsAt) return { label: 'Past', colour: 'gray' } as const;
  return { label: 'Current', colour: 'green' } as const;
};

const ServicePlanPeriodHeader: FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { project } = useGetProjectByIdOverview(projectId);
  const dateFormat = usePreferredDateFormat(DateFormats.MID);

  const period = project?.servicePlanPeriod;

  if (!period) return null;

  const planName = period.servicePlanSubscription.servicePlan.name;
  const startsAtDate = new Date(period.startsAt);
  const endsAtDate = new Date(period.endsAt);
  const startsAt = format(startsAtDate, dateFormat);
  const endsAt = format(endsAtDate, dateFormat);
  const phase = getPeriodPhase(startsAtDate, endsAtDate, new Date());

  return (
    <ViewFieldset>
      <ViewFieldset.Field label="Service Plan">{planName}</ViewFieldset.Field>
      <ViewFieldset.Field label="Period">
        <div className="flex items-center gap-2">
          <span>
            {startsAt} - {endsAt}
          </span>
          <Badge colour={phase.colour} variant="soft">
            {phase.label}
          </Badge>
        </div>
      </ViewFieldset.Field>
      <ViewFieldset.Field label="Status">
        <Badge
          colour={graphqlStatusToLocal[period.status].colour}
          variant="soft"
        >
          {graphqlStatusToLocal[period.status].label}
        </Badge>
      </ViewFieldset.Field>
    </ViewFieldset>
  );
};

export default ServicePlanPeriodHeader;
